import http from 'hub-http/clients/apiClient';
import { useQuery, registerQuery, RegisteredQuery } from 'data-fetching-client';
import { transformBackendViewToInternalFormat } from '../../viewsSearch/dataOperators/backendViews';
import { BackendView, View } from '../../viewsSearch/types/ViewTypes';
import { SupportedCategories } from '../../addView/ViewCategories';

const VIEWS_BY_CATEGORY_QUERY_NAME = 'viewsByCategory';
export const getViewsByCatagoryQueryNameForCategory = (category: string) =>
  `${VIEWS_BY_CATEGORY_QUERY_NAME}${category}`;

const VIEWS_BY_CATEGORY_ENDPOINT = 'sales/v4/views/by-category/search';
const OFFSET = 0;
const COUNT = 20;
const QUERY = '';

export type ViewsByCategoryQueryType = {
  objectTypeId: string;
  count?: number;
  query?: string;
  category: string;
  offset?: number;
  namespace?: string;
  fetchPolicy?: 'cache-first' | 'no-cache';
};

export type ViewsByCategoryResponseType = {
  viewItems: Array<View>;
  hasMore: boolean;
  offset: number;
  total: number;
};

type RawResponseType = {
  results: Array<BackendView>;
  hasMore: boolean;
  offset: number;
  total: number;
};

type RawViewsByCategoryResponseType = {
  [key: string]: RawResponseType;
};

type ViewsByCategorySearchQueriesType = {
  [key: string]: RegisteredQuery<
    string,
    ViewsByCategoryQueryType,
    ViewsByCategoryResponseType
  >;
};

const getResults = (
  category: string,
  rawViewsByCategoryResponse: RawViewsByCategoryResponseType
): BackendView[] => {
  return rawViewsByCategoryResponse[category]
    ? rawViewsByCategoryResponse[category].results
    : [];
};

const viewsByCategoryQueryArgs: (keyof Omit<
  ViewsByCategoryQueryType,
  'fetchPolicy'
>)[] = ['objectTypeId', 'category', 'count', 'query', 'offset'];

const transformRawViewsByCategoryResponse = ({
  results,
  category,
}: {
  results: RawViewsByCategoryResponseType;
  category: string;
}) => {
  const listOfViewsByCategory = getResults(category, results);
  const viewItems = listOfViewsByCategory.map((viewByCategoryItem) =>
    transformBackendViewToInternalFormat(viewByCategoryItem)
  );

  const hasMore = results[category] ? results[category].hasMore : false;
  const offset = results[category] ? results[category].offset : 0;
  const total = results[category] ? results[category].total : 0;
  return { viewItems, hasMore, offset, total };
};

const viewsByCategoryFetcher = (
  viewsByCategoryQuery: ViewsByCategoryQueryType
) => {
  return http
    .post<RawViewsByCategoryResponseType>(
      `${VIEWS_BY_CATEGORY_ENDPOINT}/${viewsByCategoryQuery.objectTypeId}`,
      {
        query: { namespace: viewsByCategoryQuery.namespace },
        data: {
          categories: [viewsByCategoryQuery.category],
          count: viewsByCategoryQuery.count,
          offset: viewsByCategoryQuery.offset,
          query: viewsByCategoryQuery.query,
        },
      }
    )
    .then((results) => {
      return transformRawViewsByCategoryResponse({
        results,
        category: viewsByCategoryQuery.category,
      });
    });
};

export const ViewsByCategorySearchQueries: ViewsByCategorySearchQueriesType =
  SupportedCategories.reduce(
    (viewsByCategorySearchQueries, category) => ({
      /* eslint-disable-next-line hubspot-dev/no-reduce-accumulator-copy */
      ...viewsByCategorySearchQueries,
      [category]: registerQuery<
        string,
        ViewsByCategoryQueryType,
        ViewsByCategoryResponseType
      >({
        fieldName: getViewsByCatagoryQueryNameForCategory(category),
        args: viewsByCategoryQueryArgs,
        fetcher: viewsByCategoryFetcher,
      }),
    }),
    {}
  );

export const useFetchViewsByCategory = ({
  objectTypeId,
  namespace,
  category,
  fetchPolicy = 'cache-first',
  query = QUERY,
  count = COUNT,
  offset = OFFSET,
}: ViewsByCategoryQueryType) => {
  return useQuery(ViewsByCategorySearchQueries[category], {
    variables: {
      objectTypeId,
      namespace,
      category,
      query,
      count,
      offset,
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy,
  });
};

export type FetchViewsByCategoryErrorType = ReturnType<
  typeof useFetchViewsByCategory
>['error'];

export type FetchViewsByCategoryLoadingType = ReturnType<
  typeof useFetchViewsByCategory
>['loading'];

export type FetchViewsByCategoryDataType = ReturnType<
  typeof useFetchViewsByCategory
>['data'];
